import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, Mail, AlertCircle } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { ThemeToggle } from "../components/ThemeToggle";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const { t, lang: language, setLang: setLanguage, availableLanguages: languages } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user) {
      navigate(user.role === "super_admin" ? "/admin" : "/events", { replace: true });
    }
  }, [user, navigate]);

  const stateError = location.state?.error;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const userData = await login(email, password);
      navigate(userData.role === "super_admin" ? "/admin" : "/events", { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || t('login.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const flagMap = { fr: "\u{1F1EB}\u{1F1F7}", en: "\u{1F1EC}\u{1F1E7}", es: "\u{1F1EA}\u{1F1F8}", ja: "\u{1F1EF}\u{1F1F5}" };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="login-page">
      {/* Theme toggle in top right */}
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>

      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img src="/wffa-logo-white.png" alt="WFFA" className="h-12 wffa-logo-adaptive" />
        </div>

        {/* Language Switcher */}
        <div className="flex justify-center gap-2 mb-6">
          {languages.map(lang => (
            <button
              key={lang}
              onClick={() => setLanguage(lang)}
              className={`text-xl transition-all rounded-md px-1 ${language === lang ? 'ring-2 ring-primary scale-110' : 'opacity-50 hover:opacity-100'}`}
            >
              {flagMap[lang] || lang}
            </button>
          ))}
        </div>

        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-foreground tracking-tight">
            {t('login.title')}
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">{t('login.subtitle')}</p>
        </div>

        <Card className="p-6 glass-card-elevated" data-testid="login-card">
          {(error || stateError) && (
            <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm" data-testid="login-error">
              <AlertCircle size={16} />
              <span>{error || stateError}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-foreground text-sm mb-1.5 block font-medium">{t('login.email')}</Label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11"
                  placeholder={t('login.emailPlaceholder')}
                  required data-testid="login-email"
                />
              </div>
            </div>
            <div>
              <Label className="text-foreground text-sm mb-1.5 block font-medium">{t('login.password')}</Label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 h-11"
                  placeholder={t('login.passwordPlaceholder')}
                  required data-testid="login-password"
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full h-11 font-semibold"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? t('login.signingIn') : t('login.signIn')}
            </Button>
            <div className="text-right">
              <a href="/forgot-password" onClick={(e) => { e.preventDefault(); navigate("/forgot-password"); }}
                className="text-sm text-primary hover:underline cursor-pointer" data-testid="forgot-password-link">
                {t('login.forgotPassword')}
              </a>
            </div>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-border" />
            <span className="text-muted-foreground text-xs uppercase tracking-wider">{t('login.or')}</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <Button
            variant="outline" onClick={handleGoogleLogin}
            className="w-full h-11"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('login.google')}
          </Button>
        </Card>

        <p className="text-center text-muted-foreground text-xs mt-6">
          {t('login.restricted')}
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
